import { useState } from 'react';
import type { VaultItem, VaultItemType } from '@heybesafe/shared';
import { useSafe } from '../context/SafeContext';
import { HbsButton } from './hbs/Button';
import { Icon } from './hbs/Icon';

interface Props {
  orgId: string;
  selfItemId?: string;
  value: string[];
  onChange: (next: string[]) => void;
}

// Binder types plus the family-shared Safe types. 'passkey' is deliberately
// absent: a passkey belongs to a login rather than standing on its own, so
// linking one would surface an item the family Safe never presents alone.
// Must stay in step with LINKABLE_SAFE_TYPES in binder/resolveLinkedItems.ts —
// anything offered here that the resolver drops would vanish on save.
const LINKABLE: VaultItemType[] = ['contact', 'document', 'schedule', 'login', 'note', 'card', 'identity'];
const TYPE_LABEL: Record<string, string> = {
  contact: 'Contacts', document: 'Documents', schedule: 'Schedules',
  login: 'Logins', note: 'Notes', card: 'Cards', identity: 'Identities',
};
// Selected-chip ordering: contacts, documents, then Safe items, then schedules
// (schedules sink to the bottom, matching the read-only LinkedItemsSection order).
const TYPE_RANK: Record<string, number> = {
  contact: 0, document: 1, login: 2, note: 3, card: 4, identity: 5, schedule: 6,
};

/**
 * Picker for a document's linked items (`relatedItemIds`). Offers org-scoped
 * Binder items (contacts / documents / schedules) and family-shared Safe items
 * (logins / notes / cards / identities) in the same family, excluding the
 * document being edited. Selected ids show as removable chips.
 *
 * Candidates are already restricted to `orgId`, so this opens no new sharing
 * surface: only items the family can already see are linkable, and a viewer who
 * can see the document can open them anyway.
 */
export default function LinkedItemsField({ orgId, selfItemId, value, onChange }: Props): JSX.Element {
  const { items } = useSafe();
  const [open, setOpen] = useState(false);

  const candidates = items.filter(
    (i) => i.orgId === orgId && i.id !== selfItemId && LINKABLE.includes(i.type),
  );
  const selected = value
    .map((id) => candidates.find((i) => i.id === id))
    .filter((i): i is VaultItem => !!i)
    // Stable sort by type rank keeps contacts/documents in their selection
    // order while pushing schedules to the end.
    .sort((a, b) => (TYPE_RANK[a.type] ?? 0) - (TYPE_RANK[b.type] ?? 0));

  function toggle(id: string): void {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  }

  const grouped = LINKABLE.map((t) => ({ type: t, rows: candidates.filter((i) => i.type === t && !value.includes(i.id)) }))
    .filter((g) => g.rows.length > 0);

  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="text-xs font-medium text-hbs-text-mute uppercase tracking-hbs-overline">
        Linked items
      </legend>

      {selected.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {selected.map((i) => (
            <li key={i.id} className="inline-flex items-center gap-1 bg-hbs-surface-alt border border-hbs-border rounded-hbs-sm px-2 py-1 text-[13px] text-hbs-text">
              {i.name}
              <button
                type="button"
                aria-label={`Remove ${i.name}`}
            id={`linked-item-remove-${i.id}`}
            onClick={() => toggle(i.id)}
                className="text-hbs-text-mute hover:text-hbs-text"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <HbsButton variant="ghost" type="button" id="linked-item-add-button" onClick={() => setOpen((o) => !o)}>
        <Icon name="launch" size={14} /> Add linked item
      </HbsButton>

      {open && (
        <div className="border border-hbs-border rounded-hbs-sm p-3 flex flex-col gap-3">
          {grouped.length === 0 && <p className="text-[13px] text-hbs-text-mute">Nothing else to link yet.</p>}
          {grouped.map((g) => (
            <div key={g.type} className="flex flex-col gap-1">
              <div className="text-[11.5px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute">{TYPE_LABEL[g.type]}</div>
              {g.rows.map((i) => (
                <button
                  key={i.id}
                  type="button"
                id={`linked-item-option-${i.id}`}
                onClick={() => { toggle(i.id); }}
                  className="text-left text-[14px] text-hbs-text hover:text-hbs-primary hover:underline"
                >
                  {i.name}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </fieldset>
  );
}
